import { header } from 'utils/auth';
import { request } from 'utils/request';

const jsonHeaders = () =>
  new Headers({
    'Content-Type': 'application/json',
    Authorization: header(),
  });

const assertOk = (res: any, fallbackMsg: string) => {
  const status = res?.statusCode ?? res?.myAutoIQ_status_code ?? res?.errorCode;
  const msg = res?.message ?? res?.myAutoIQ_error_message;
  if (typeof status === 'number' && status >= 400) {
    throw new Error(msg || fallbackMsg);
  }
  return res;
};

export interface LeadAnalysisSegmentBreakdown {
  segment: string;
  count: number;
  percentage: number;
}

export interface LeadAnalysisMetric<T> {
  value: T;
  sql: string;
}

export interface LeadAnalysisResponse {
  dealer_id: string;
  look_back_date: string | null;
  look_back_start_date: string | null;
  look_back_end_date: string | null;
  launch_date: string | null;
  launch_date_available: boolean;
  total_available_leads: LeadAnalysisMetric<number>;
  look_back_months: LeadAnalysisMetric<number>;
  average_leads_per_month: LeadAnalysisMetric<number>;
  group_by_segment: LeadAnalysisMetric<LeadAnalysisSegmentBreakdown[]>;
  messages: string[];
}

const extractResponseBody = (res: any): LeadAnalysisResponse => {
  const body = res?.body ?? res?.data ?? res;
  return body as LeadAnalysisResponse;
};

/**
 * Lead analysis for a dealer scoped to a look back date range.
 *
 * GET /api/v2/leads/analysis?dealer_id=&look_back_start_date=&look_back_end_date=
 */
export const fetchLeadAnalysis = async ({
  dealerId,
  lookBackStartDate,
  lookBackEndDate,
}: {
  dealerId: string;
  lookBackStartDate?: string | null;
  lookBackEndDate?: string | null;
}): Promise<LeadAnalysisResponse> => {
  const params = new URLSearchParams({
    dealer_id: dealerId,
  });
  if (lookBackStartDate) {
    params.set('look_back_start_date', lookBackStartDate);
  }
  if (lookBackEndDate) {
    params.set('look_back_end_date', lookBackEndDate);
  }
  const requestURL = `/api/v2/leads/analysis?${params.toString()}`;
  const response: any = await request(requestURL, {
    method: 'GET',
    headers: jsonHeaders(),
  });
  if (!response) {
    throw new Error('Failed to fetch lead analysis');
  }
  const res = assertOk(await response, 'Failed to fetch lead analysis');
  return extractResponseBody(res);
};
